// VisualizePage - Main 3D visualization page
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTheme } from 'next-themes';
import { HelpCircle } from 'lucide-react';
import { TopNav } from '../ui/top-nav';
import { toast } from 'sonner';
import { lambdaService, FileInfo } from '../../services/lambdaService';
import { fetchAndDecompressS3, shouldFetchFromS3 } from '../../utils/s3Utils';
import Scene3D from './Scene3D';
import ControlPanel from './ControlPanel';
import ViewControls from './ViewControls';
import CameraControls from './CameraControls';
import QueryPanel from './QueryPanel';
import { DataOverlay, HoverCard, FullDetailOverlay } from './Overlays';
import {
  DataRow,
  LayoutType,
  PaletteType,
  ThemeType,
  ShapeType,
  SortDirection,
  SizeDimension,
  ViewSettings,
  Position3D,
  VisualizationSessionData,
} from '../../types/visualization';
import {
  THEMES,
  DEFAULT_VIEW_SETTINGS,
  DEFAULT_VISUALIZATION_SETTINGS,
  rowMatchesSearch,
} from '../../constants/visualizationThemes';
import * as THREE from 'three';

// IIS log sample data — groupable columns for cycling demo
const SAMPLE_DATA: DataRow[] = (() => {
  const methods = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'];
  const methodWeights = [6000, 5200, 5800, 5100, 5400]; // asymmetric, 5000+ per group
  const statusCodes = ['200', '301', '304', '400', '401', '403', '404', '500', '502', '503'];
  const statusWeights: Record<string, number> = { '200': 45, '301': 8, '304': 12, '400': 10, '401': 5, '403': 3, '404': 10, '500': 4, '502': 2, '503': 1 };
  const uriStems = ['/api/v1/users', '/api/v1/orders', '/api/v1/products', '/api/v1/search', '/api/v1/auth', '/static/js/bundle.js', '/static/css/main.css', '/health', '/api/v2/analytics', '/api/v1/webhooks'];
  const userAgents = ['Chrome/121', 'Firefox/122', 'Safari/17', 'Edge/121', 'Bot/Googlebot', 'Bot/Bingbot', 'Python-requests/2.31', 'curl/8.4'];
  const serverNames = ['web-prod-01', 'web-prod-02', 'web-prod-03', 'api-prod-01', 'api-prod-02'];

  const rows: DataRow[] = [];
  let id = 0;
  const pick = <T,>(arr: T[]) => arr[Math.floor(Math.random() * arr.length)];
  const weightedStatus = () => {
    const r = Math.random() * 100;
    let cum = 0;
    for (const [code, w] of Object.entries(statusWeights)) { cum += w; if (r < cum) return code; }
    return '200';
  };

  methods.forEach((method, mi) => {
    for (let i = 0; i < methodWeights[mi]; i++) {
      const sc = weightedStatus();
      rows.push({
        _id: id++,
        method,
        status_code: sc,
        uri_stem: pick(uriStems),
        user_agent: pick(userAgents),
        server: pick(serverNames),
        bytes_sent: Math.round(200 + Math.random() * 50000),
        time_taken_ms: sc.startsWith('5') ? Math.round(500 + Math.random() * 4500) : Math.round(5 + Math.random() * 300),
        client_ip: `10.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}`,
      });
    }
  });
  return rows;
})();

// Columns the sample demo cycles through for grouping
const SAMPLE_GROUPINGS = ['method', 'status_code', 'server', 'uri_stem'];

const SESSION_STORAGE_KEY = 'bigsplice-visualize-data';
const SESSION_VALIDITY_MS = 5 * 60 * 1000; // 5 minutes

// Helper to clean file names for display - hide internal implementation details
const getDisplayName = (fileName: string): string => {
  let name = fileName;
  // Remove common extensions
  name = name.replace(/\.(parquet|csv|json|xlsx|xls|tsv)$/i, '');
  // Remove double extensions like .csv.parquet
  name = name.replace(/\.(csv|json)\.(parquet)$/i, '');
  // Remove timestamp prefix (e.g., "1765940893209-")
  if (/^\d+-/.test(name)) {
    name = name.replace(/^\d+-/, '');
  }
  // Remove date-time suffix (e.g., "_20231215_143022")
  name = name.replace(/_\d{8}_\d{6}$/, '');
  // Remove "(X files)" or "(X parts)" suffixes
  name = name.replace(/\s*\(\d+\s+(files?|parts?)\)$/i, '');
  // Remove "_part_X" or "_chunk_X" suffixes
  name = name.replace(/[_-](part|chunk)[_-]?\d+$/i, '');
  // Remove trailing underscores or hyphens
  name = name.replace(/[_-]+$/, '');
  return name;
};

// Pinned search term type
interface PinnedSearch {
  term: string;
  color: string;
}

// Color palette for auto-assigning colors to pinned searches
const SEARCH_COLORS = [
  '#ff6b6b', // red
  '#4ecdc4', // teal
  '#45b7d1', // blue
  '#96ceb4', // green
  '#ffeaa7', // yellow
  '#dfe6e9', // gray
  '#fd79a8', // pink
  '#a29bfe', // purple
  '#00b894', // emerald
  '#fdcb6e', // orange
];

export default function VisualizePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { theme: globalTheme } = useTheme();

  // Data state
  const [allData, setAllData] = useState<DataRow[]>([]);
  const [columns, setColumns] = useState<string[]>([]);
  const [columnCardinalities, setColumnCardinalities] = useState<Record<string, number>>({});
  const [numericColumns, setNumericColumns] = useState<string[]>([]);

  // Loading state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isLoadingQuery, setIsLoadingQuery] = useState(false);

  // Ref to immediately abort demo search from outside the effect (e.g. when processData is called)
  const demoSearchAbortRef = useRef(false);

  // File listing state
  const [files, setFiles] = useState<FileInfo[]>([]);
  const [filesLoading, setFilesLoading] = useState(true);
  const [selectedFile, setSelectedFile] = useState<string>('');
  const [query, setQuery] = useState<string>('result = df.lazy().head(10000).collect()');
  const [availableColumns, setAvailableColumns] = useState<string[]>([]);
  const [isLoadingColumns, setIsLoadingColumns] = useState(false);

  // UI state
  const [selectedData, setSelectedData] = useState<DataRow | null>(null);
  const [isDataOverlayMinimized, setIsDataOverlayMinimized] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [hoverCardVisible, setHoverCardVisible] = useState(false);
  const [fullDetailData, setFullDetailData] = useState<DataRow | null>(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [showHelp, setShowHelp] = useState(false);

  // Visualization settings
  const [layout, setLayout] = useState<LayoutType>(DEFAULT_VISUALIZATION_SETTINGS.layout);
  const [xAxis, setXAxis] = useState<string>('');
  const [sortColumn, setSortColumn] = useState<string>('originalIndex');
  const [sortDirection, setSortDirection] = useState<SortDirection>(DEFAULT_VISUALIZATION_SETTINGS.sortDirection);
  const [colorColumn, setColorColumn] = useState<string>('');
  const [colorPalette, setColorPalette] = useState<PaletteType>(DEFAULT_VISUALIZATION_SETTINGS.colorPalette);
  const [searchQuery, setSearchQuery] = useState('');
  const [vizTheme, setVizTheme] = useState<ThemeType>(DEFAULT_VISUALIZATION_SETTINGS.theme);

  // Sync viz theme with global theme toggle
  useEffect(() => {
    if (globalTheme === 'light' || globalTheme === 'dark') {
      setVizTheme(globalTheme as ThemeType);
    }
  }, [globalTheme]);

  // Scatter plot columns
  const [scatterXColumn, setScatterXColumn] = useState<string>('');
  const [scatterYColumn, setScatterYColumn] = useState<string>('');

  // Search highlight settings
  const [searchHighlightColor, setSearchHighlightColor] = useState(DEFAULT_VISUALIZATION_SETTINGS.searchHighlightColor);
  const [cubeMagnification, setCubeMagnification] = useState(DEFAULT_VISUALIZATION_SETTINGS.cubeMagnification);

  // Multi-term search with pinned colors
  const [pinnedSearches, setPinnedSearches] = useState<PinnedSearch[]>([]);

  // Effects settings
  const [metallicMode, setMetallicMode] = useState(DEFAULT_VISUALIZATION_SETTINGS.metallicMode);

  // Shape and size settings
  const [shapeType, setShapeType] = useState<ShapeType>(DEFAULT_VISUALIZATION_SETTINGS.shapeType);
  const [sizeByColumn, setSizeByColumn] = useState<string | null>(null);
  const [sizeByDimension, setSizeByDimension] = useState<SizeDimension>(DEFAULT_VISUALIZATION_SETTINGS.sizeByDimension);
  const [maxSizeMultiplier, setMaxSizeMultiplier] = useState(DEFAULT_VISUALIZATION_SETTINGS.maxSizeMultiplier);

  // View settings
  const [viewSettings, setViewSettings] = useState<ViewSettings>(DEFAULT_VIEW_SETTINGS);

  // Sample data grouping cycle — switches xAxis 5 times then stops
  // On 3rd switch (index 2), transitions to pie layout with top-down camera
  const [sampleGroupIdx, setSampleGroupIdx] = useState(0);
  const [sampleLayout, setSampleLayout] = useState<LayoutType>('force');
  const sampleCycleCount = useRef(0);
  const cameraAnimRef = useRef<number | null>(null);

  // Smooth camera transition helper
  const animateCamera = useCallback((targetPos: [number, number, number], duration: number = 1500) => {
    if (!controlsRef.current) return;
    const controls = controlsRef.current;
    const camera = controls.object;
    const startPos = { x: camera.position.x, y: camera.position.y, z: camera.position.z };
    const startTime = performance.now();

    const animate = () => {
      const elapsed = performance.now() - startTime;
      const progress = Math.min(1, elapsed / duration);
      const ease = 1 - Math.pow(1 - progress, 3); // cubic ease-out

      camera.position.x = startPos.x + (targetPos[0] - startPos.x) * ease;
      camera.position.y = startPos.y + (targetPos[1] - startPos.y) * ease;
      camera.position.z = startPos.z + (targetPos[2] - startPos.z) * ease;
      controls.update();

      if (progress < 1) {
        cameraAnimRef.current = requestAnimationFrame(animate);
      }
    };
    if (cameraAnimRef.current) cancelAnimationFrame(cameraAnimRef.current);
    cameraAnimRef.current = requestAnimationFrame(animate);
  }, []);

  useEffect(() => {
    if (allData.length > 0) return;
    if (sampleCycleCount.current >= 5) return;
    const timer = setInterval(() => {
      sampleCycleCount.current += 1;
      if (sampleCycleCount.current >= 5) {
        clearInterval(timer);
        return;
      }
      const nextIdx = (sampleCycleCount.current) % SAMPLE_GROUPINGS.length;
      setSampleGroupIdx(nextIdx);

      // On 3rd switch, change to pie layout with top-down camera
      if (sampleCycleCount.current === 2) {
        setSampleLayout('donut');
        animateCamera([0, 25, 3], 2000);
      } else if (sampleCycleCount.current === 3) {
        // Switch back to force on 4th
        setSampleLayout('force');
        animateCamera([0, 4.5, 18], 2000);
      }
    }, 7000);
    return () => clearInterval(timer);
  }, [allData.length, animateCamera]);

  // Demo search auto-type — types a search term during sample animation
  const [demoSearchText, setDemoSearchText] = useState('');
  const demoSearchTerms = ['502', 'webhooks', 'Bingbot'];
  const demoSearchIdx = useRef(0);
  useEffect(() => {
    if (allData.length > 0) {
      // Data loaded — permanently disable demo search
      demoSearchAbortRef.current = true;
      setDemoSearchText('');
      setSearchQuery('');
      return;
    }
    // If data was ever loaded (e.g. resetVisualizationState set allData=[]), don't restart
    if (demoSearchAbortRef.current) {
      setDemoSearchText('');
      setSearchQuery('');
      return;
    }

    let aborted = false;
    const isAborted = () => aborted || demoSearchAbortRef.current;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const intervals: ReturnType<typeof setInterval>[] = [];

    const startDelay = setTimeout(() => {
      if (isAborted()) return;
      const runDemoSearch = () => {
        if (isAborted()) return;
        const term = demoSearchTerms[demoSearchIdx.current % demoSearchTerms.length];
        let charIdx = 0;
        const typeTimer = setInterval(() => {
          if (isAborted()) {
            clearInterval(typeTimer);
            setDemoSearchText('');
            setSearchQuery('');
            return;
          }
          charIdx++;
          if (charIdx <= term.length) {
            setDemoSearchText(term.slice(0, charIdx));
            setSearchQuery(term.slice(0, charIdx));
          } else {
            clearInterval(typeTimer);
            const pauseTimer = setTimeout(() => {
              if (isAborted()) return;
              setDemoSearchText('');
              setSearchQuery('');
              demoSearchIdx.current++;
              if (demoSearchIdx.current < demoSearchTerms.length) {
                const nextTimer = setTimeout(runDemoSearch, 2000);
                timers.push(nextTimer);
              }
            }, 3500);
            timers.push(pauseTimer);
          }
        }, 250);
        intervals.push(typeTimer);
      };
      runDemoSearch();
    }, 4000);
    timers.push(startDelay);

    return () => {
      aborted = true;
      timers.forEach(clearTimeout);
      intervals.forEach(clearInterval);
    };
  }, [allData.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refs
  const controlsRef = useRef<any>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Load files on mount
  useEffect(() => {
    const loadFiles = async () => {
      setFilesLoading(true);
      try {
        const response = await lambdaService.listFiles();
        if (response.files) {
          setFiles(response.files);
        }
      } catch (err) {
        console.error('Failed to load files:', err);
      } finally {
        setFilesLoading(false);
      }
    };
    loadFiles();
  }, []);

  // Check for data from sessionStorage (from Analyze page)
  useEffect(() => {
    const source = searchParams.get('source');
    if (source === 'analyze') {
      try {
        const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
        if (stored) {
          const sessionData: VisualizationSessionData = JSON.parse(stored);
          if (Date.now() - sessionData.timestamp < SESSION_VALIDITY_MS) {
            processData(sessionData.data);
            setLoading(false);
            sessionStorage.removeItem(SESSION_STORAGE_KEY);
            toast.success(`Loaded ${sessionData.data.length.toLocaleString()} rows from analysis`);
            return;
          }
        }
      } catch (err) {
        console.error('Failed to load session data:', err);
      }
    }
    setLoading(false);
  }, [searchParams]);

  // Reset visualization state before loading new data
  const resetVisualizationState = useCallback(() => {
    // Clear all data and UI state to prevent stale references
    setAllData([]);
    setColumns([]);
    setColumnCardinalities({});
    setNumericColumns([]);
    setSelectedData(null);
    setHoveredIndex(null);
    setHoverCardVisible(false);
    setFullDetailData(null);
    setSearchQuery('');
    setPinnedSearches([]);
    setError(null);
    // Reset column-specific settings that reference old data columns
    setXAxis('');
    setColorColumn('');
    setSortColumn('originalIndex');
    setScatterXColumn('');
    setScatterYColumn('');
    setSizeByColumn(null);
  }, []);

  // Process incoming data
  const processData = useCallback((data: DataRow[]) => {
    if (!data || data.length === 0) {
      setError('No data available');
      return;
    }

    // Kill demo search immediately — the ref is checked by the interval on every tick
    demoSearchAbortRef.current = true;
    setDemoSearchText('');

    // Reset state first to prevent stale references causing crashes
    resetVisualizationState();

    // Add internal ID to each row
    const processedData: DataRow[] = data.map((row, idx) => ({ ...row, _id: idx }));

    // Use setTimeout to ensure state is cleared before setting new data
    setTimeout(() => {
      setAllData(processedData);

      // Extract columns from first row
      const cols = Object.keys(data[0]).filter(k => k !== '_id');
      setColumns(cols);

      // Calculate cardinality for each column
      const cardinalities: Record<string, number> = {};
      cols.forEach(col => {
        const uniqueValues = new Set(processedData.map(row => row[col]));
        cardinalities[col] = uniqueValues.size;
      });
      setColumnCardinalities(cardinalities);

      // Detect numeric columns
      const numCols = cols.filter(col => {
        const samples = processedData.slice(0, 100)
          .map(row => row[col])
          .filter(v => v !== '' && v !== null && v !== undefined);
        if (samples.length === 0) return false;
        const numericCount = samples.filter(v => {
          const num = parseFloat(String(v));
          return !isNaN(num) && Number.isFinite(num);
        }).length;
        return numericCount / samples.length > 0.8;
      });
      setNumericColumns(numCols);

      // Set initial axes - prefer lower cardinality for grouping
      const sortedByCardinality = [...cols].sort((a, b) => (cardinalities[a] || 0) - (cardinalities[b] || 0));
      const goodGroupingCol = sortedByCardinality.find(col => cardinalities[col] > 1 && cardinalities[col] < 50) || cols[0];
      setXAxis(goodGroupingCol);
      setColorColumn(goodGroupingCol);

      // Set scatter columns to first numeric columns
      if (numCols.length > 0) {
        setScatterXColumn(numCols[0]);
        setScatterYColumn(numCols[1] || numCols[0]);
      }

      setError(null);
    }, 0);
  }, [resetVisualizationState]);

  // Handle file selection - fetch columns using head(1)
  const handleFileSelect = useCallback(async (fileKey: string) => {
    setSelectedFile(fileKey);
    setAvailableColumns([]);

    if (!fileKey) return;

    setIsLoadingColumns(true);
    try {
      // Use head(1) to quickly get column names
      const result = await lambdaService.runAnalysis(
        fileKey,
        'result = df.lazy().head(1).collect()',
        1,
        'first',
        'file',
        1,
        1
      );

      if (result?.result && result.result.length > 0) {
        const cols = Object.keys(result.result[0]).filter(k => k !== 'originalIndex');
        setAvailableColumns(cols);
      }
    } catch (err) {
      console.error('Failed to fetch columns:', err);
      // Don't show error toast - columns will just be empty
    } finally {
      setIsLoadingColumns(false);
    }
  }, []);

  // Execute query on file
  const handleExecuteQuery = useCallback(async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }

    setIsLoadingQuery(true);
    setError(null);

    try {
      // Use 'all' to process all chunks for multipart files, increase limits for visualization
      const result = await lambdaService.runAnalysis(selectedFile, query, 100, 'all', 'file', 100000, 100000);

      if (!result) {
        setError('No results returned');
        toast.error('Query returned no results');
        return;
      }

      // Check if we need to fetch full results (s3Only mode or truncated results)
      if (shouldFetchFromS3(result)) {
        if (!result.s3Results?.display_url) {
          throw new Error('Unable to load full results');
        }

        const rowCount = result.s3Results.full_row_count || result.totalRowsAvailable || 0;
        toast.info(`Loading ${rowCount.toLocaleString()} rows...`);

        try {
          const fullData = await fetchAndDecompressS3(result.s3Results.display_url);
          processData(fullData);
          toast.success(`Loaded ${fullData.length.toLocaleString()} rows in ${(result.executionTime / 1000).toFixed(2)}s`);
        } catch (fetchError) {
          console.error('Failed to load full results:', fetchError);
          // Fallback to inline results if available
          if (result.result && result.result.length > 0) {
            processData(result.result);
            toast.warning(`Using partial results (${result.rowCount.toLocaleString()} of ${rowCount.toLocaleString()} rows)`);
          } else {
            throw new Error('Failed to load data');
          }
        }
      } else if (result.result && result.result.length > 0) {
        // Use inline results
        processData(result.result);
        toast.success(`Loaded ${result.rowCount.toLocaleString()} rows in ${(result.executionTime / 1000).toFixed(2)}s`);
      } else {
        setError('No results returned');
        toast.error('Query returned no results');
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to execute query';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsLoadingQuery(false);
    }
  }, [selectedFile, query, processData]);

  // Handle hover card visibility
  useEffect(() => {
    if (hoveredIndex !== null) {
      hoverTimeoutRef.current = setTimeout(() => {
        setHoverCardVisible(true);
      }, 1000);
    } else {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
      setHoverCardVisible(false);
    }
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, [hoveredIndex]);

  // Auto-zoom camera when layout changes
  useEffect(() => {
    if (layout === 'force' && controlsRef.current) {
      const controls = controlsRef.current;
      const camera = controls.object;
      camera.position.set(0, 8, 20);
      controls.target.set(0, 0, 0);
      controls.update();
    }
  }, [layout]);

  // Handle view setting changes
  const handleViewChange = useCallback((key: keyof ViewSettings, value: boolean | number) => {
    setViewSettings(prev => ({ ...prev, [key]: value }));
  }, []);

  // Reset camera
  const handleResetCamera = useCallback(() => {
    if (controlsRef.current) {
      controlsRef.current.reset();
    }
  }, []);

  // Handle shift+click for full detail
  const handleShiftClick = useCallback((data: DataRow, position: Position3D | null) => {
    setFullDetailData(data);
    if (position && controlsRef.current) {
      const controls = controlsRef.current;
      const camera = controls.object;
      const distance = 1.5;
      const targetPos = new THREE.Vector3(position.x, position.y, position.z);
      const cameraPos = new THREE.Vector3(
        position.x + distance * 0.3,
        position.y + distance * 0.5,
        position.z + distance
      );
      camera.position.copy(cameraPos);
      controls.target.copy(targetPos);
      controls.update();
    }
  }, []);

  // Close full detail overlay
  const handleCloseFullDetail = useCallback(() => {
    setFullDetailData(null);
  }, []);

  // Pin current search term with auto-assigned color
  const handlePinSearch = useCallback(() => {
    if (!searchQuery.trim()) return;
    // Check if term already pinned
    if (pinnedSearches.some(p => p.term.toLowerCase() === searchQuery.toLowerCase())) {
      toast.info('Term already pinned');
      return;
    }
    // Get next available color
    const usedColors = new Set(pinnedSearches.map(p => p.color));
    const nextColor = SEARCH_COLORS.find(c => !usedColors.has(c)) || SEARCH_COLORS[pinnedSearches.length % SEARCH_COLORS.length];
    setPinnedSearches(prev => [...prev, { term: searchQuery.trim(), color: nextColor }]);
    setSearchQuery(''); // Clear the input after pinning
  }, [searchQuery, pinnedSearches]);

  // Remove a pinned search
  const handleRemovePinnedSearch = useCallback((term: string) => {
    setPinnedSearches(prev => prev.filter(p => p.term !== term));
  }, []);

  // Search match count for current input
  const searchMatchCount = useMemo(() => {
    if (!searchQuery) return 0;
    return allData.filter(row => rowMatchesSearch(row, searchQuery)).length;
  }, [allData, searchQuery]);

  // Match counts for pinned searches
  const pinnedMatchCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    pinnedSearches.forEach(p => {
      counts[p.term] = allData.filter(row => rowMatchesSearch(row, p.term)).length;
    });
    return counts;
  }, [allData, pinnedSearches]);

  // Track mouse position
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePos({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // ESC key to close overlays
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setFullDetailData(null);
        setSelectedData(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const hoveredData = hoveredIndex !== null ? allData[hoveredIndex] : null;
  const theme = vizTheme; // alias for passing to children
  const t = THEMES[vizTheme];
  const isDark = vizTheme === 'dark';

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <TopNav />
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 'calc(100vh - 4.75rem)',
          background: t.background,
          color: t.text,
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '24px', marginBottom: '16px' }}>Loading...</div>
            <div style={{ color: t.textMuted }}>Initializing visualization</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <TopNav />
    <div style={{
      width: '100%',
      height: 'calc(100vh - 4.75rem)',
      position: 'relative',
      overflow: 'hidden',
      background: t.background,
    }}>
      {/* 3D Scene — always rendered, uses sample data when no user data loaded */}
      <Scene3D
        allData={allData.length > 0 ? allData : SAMPLE_DATA}
        layout={allData.length > 0 ? layout : sampleLayout}
        xAxis={allData.length > 0 ? xAxis : SAMPLE_GROUPINGS[sampleGroupIdx]}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        colorColumn={allData.length > 0 ? colorColumn : SAMPLE_GROUPINGS[sampleGroupIdx]}
        colorPalette={colorPalette}
        searchQuery={searchQuery}
        pinnedSearches={pinnedSearches}
        setSelectedData={allData.length > 0 ? setSelectedData : () => {}}
        onShiftClick={allData.length > 0 ? handleShiftClick : () => {}}
        hoveredIndex={hoveredIndex}
        setHoveredIndex={allData.length > 0 ? setHoveredIndex : () => {}}
        viewSettings={allData.length > 0 ? viewSettings : { ...viewSettings, autoRotate: true, rotationSpeed: 0.15, animSpeed: 0.5, showGrid: false }}
        cameraPosition={allData.length > 0 ? undefined : [0, 4.5, 18]}
        controlsRef={controlsRef}
        theme={theme}
        scatterXColumn={scatterXColumn}
        scatterYColumn={scatterYColumn}
        searchHighlightColor={searchHighlightColor}
        cubeMagnification={cubeMagnification}
        metallicMode={metallicMode}
        shapeType={shapeType}
        sizeByColumn={sizeByColumn}
        sizeByDimension={sizeByDimension}
        maxSizeMultiplier={maxSizeMultiplier}
      />

      {/* Overlay message when showing sample data */}
      {allData.length === 0 && !loading && (
        <div style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 50,
          pointerEvents: 'none',
        }}>
          <div style={{
            textAlign: 'center',
            background: isDark
              ? 'rgba(0,0,0,0.6)'
              : 'rgba(255,255,255,0.75)',
            backdropFilter: 'blur(12px)',
            borderRadius: '12px',
            padding: '24px 36px',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
          }}>
            <h2 style={{
              margin: 0,
              fontSize: '18px',
              fontWeight: 600,
              color: isDark ? '#fff' : '#1d1d1f',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            }}>
              Select a file and load data
            </h2>
            <p style={{
              margin: '6px 0 0',
              fontSize: '13px',
              color: t.textMuted,
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            }}>
              Use the query panel to choose a file and run a query
            </p>
          </div>
        </div>
      )}

      {/* Demo search box — auto-types search terms during sample animation */}
      {allData.length === 0 && !loading && (
        <div style={{
          position: 'absolute',
          bottom: '28px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 60,
          pointerEvents: 'none',
          opacity: demoSearchText ? 1 : 0,
          transition: 'opacity 0.4s ease',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            background: isDark
              ? 'rgba(28, 32, 42, 0.95)'
              : 'rgba(255, 255, 255, 0.96)',
            backdropFilter: 'blur(24px) saturate(1.4)',
            borderRadius: '8px',
            padding: '8px 14px',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.12)'}`,
            boxShadow: isDark
              ? '0 8px 32px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.06) inset'
              : '0 8px 32px rgba(0,0,0,0.08), 0 0 0 1px rgba(255,255,255,0.8) inset',
            gap: '10px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={t.textMuted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/>
              <path d="m21 21-4.35-4.35"/>
            </svg>
            <span style={{
              fontSize: '12px',
              color: isDark ? '#fff' : '#1d1d1f',
              fontFamily: 'SF Mono, Menlo, Consolas, monospace',
              minWidth: '80px',
            }}>
              {demoSearchText}
              <span style={{
                display: 'inline-block',
                width: '1px',
                height: '14px',
                background: '#3b82f6',
                marginLeft: '1px',
                verticalAlign: 'middle',
                animation: 'blink 1s step-end infinite',
              }} />
            </span>
            <span style={{ fontSize: '10px', color: '#3b82f6', fontWeight: 500 }}>
              highlighting matches
            </span>
          </div>
        </div>
      )}

      {/* Data Origin Card — ReactFlow-style node, bottom-left */}
      {allData.length === 0 && !loading && (
        <div style={{
          position: 'absolute',
          bottom: '80px',
          left: '300px',
          zIndex: 60,
          pointerEvents: 'none',
          animation: 'fadeSlideUp 0.6s ease-out both',
          animationDelay: '0.8s',
        }}>
          <div style={{
            width: '260px',
            background: isDark
              ? 'rgba(22, 26, 35, 0.92)'
              : 'rgba(255, 255, 255, 0.94)',
            backdropFilter: 'blur(20px) saturate(1.3)',
            borderRadius: '8px',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.1)'}`,
            boxShadow: isDark
              ? '0 8px 32px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.04) inset'
              : '0 8px 32px rgba(0,0,0,0.08), 0 0 0 1px rgba(255,255,255,0.7) inset',
            overflow: 'hidden',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          }}>
            {/* Node handle bar */}
            <div style={{
              height: '3px',
              background: 'linear-gradient(90deg, #3b82f6, #6366f1)',
            }} />
            {/* Header */}
            <div style={{
              padding: '8px 10px 6px',
              display: 'flex',
              alignItems: 'center',
              gap: '7px',
              borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
            }}>
              <div style={{
                width: '22px',
                height: '22px',
                borderRadius: '5px',
                background: isDark ? 'rgba(59,130,246,0.15)' : 'rgba(59,130,246,0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <ellipse cx="12" cy="5" rx="9" ry="3"/>
                  <path d="M3 5v14a9 3 0 0 0 18 0V5"/>
                  <path d="M3 12a9 3 0 0 0 18 0"/>
                </svg>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '11px', fontWeight: 600, color: isDark ? '#e2e8f0' : '#1e293b', lineHeight: 1.2 }}>
                  IIS Web Server Logs
                </div>
                <div style={{ fontSize: '9px', color: t.textMuted, lineHeight: 1.3 }}>
                  {SAMPLE_DATA.length.toLocaleString()} requests • 5 servers
                </div>
              </div>
              <div style={{
                marginLeft: 'auto',
                padding: '2px 6px',
                borderRadius: '4px',
                background: isDark ? 'rgba(59,130,246,0.15)' : 'rgba(59,130,246,0.1)',
                border: `1px solid ${isDark ? 'rgba(59,130,246,0.3)' : 'rgba(59,130,246,0.2)'}`,
                fontSize: '8px',
                fontWeight: 700,
                letterSpacing: '0.5px',
                color: '#3b82f6',
                textTransform: 'uppercase' as const,
                whiteSpace: 'nowrap' as const,
              }}>
                Sample Data
              </div>
            </div>
            {/* Mini spreadsheet */}
            <div style={{ padding: '0' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '8.5px',
                fontFamily: 'SF Mono, Menlo, Consolas, monospace',
                lineHeight: 1.4,
              }}>
                <thead>
                  <tr style={{
                    background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                  }}>
                    {['method', 'status', 'uri_stem', 'ms'].map(h => (
                      <th key={h} style={{
                        padding: '4px 5px 3px',
                        textAlign: 'left',
                        fontWeight: 600,
                        color: isDark ? 'rgba(148,163,184,0.8)' : 'rgba(100,116,139,0.9)',
                        borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
                        textTransform: 'uppercase',
                        fontSize: '7.5px',
                        letterSpacing: '0.3px',
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    { m: 'GET',    s: '200', u: '/api/v1/users',   ms: '12' },
                    { m: 'POST',   s: '201', u: '/api/v1/orders',  ms: '45' },
                    { m: 'GET',    s: '304', u: '/static/js/bun…', ms: '3' },
                    { m: 'DELETE', s: '404', u: '/api/v1/webhoo…', ms: '18' },
                    { m: 'PUT',    s: '502', u: '/api/v2/analyt…', ms: '4210' },
                    { m: 'GET',    s: '200', u: '/health',         ms: '2' },
                  ].map((row, i) => (
                    <tr key={i} style={{
                      borderBottom: i < 5 ? `1px solid ${isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)'}` : 'none',
                    }}>
                      <td style={{ padding: '3px 5px', color: isDark ? '#93c5fd' : '#2563eb', fontWeight: 500 }}>{row.m}</td>
                      <td style={{
                        padding: '3px 5px',
                        color: row.s.startsWith('2') ? (isDark ? '#6ee7b7' : '#059669')
                             : row.s.startsWith('3') ? (isDark ? '#a5b4fc' : '#6366f1')
                             : row.s.startsWith('4') ? (isDark ? '#fbbf24' : '#d97706')
                             : (isDark ? '#f87171' : '#dc2626'),
                        fontWeight: 500,
                      }}>{row.s}</td>
                      <td style={{ padding: '3px 5px', color: isDark ? 'rgba(226,232,240,0.7)' : 'rgba(30,41,59,0.7)' }}>{row.u}</td>
                      <td style={{
                        padding: '3px 5px',
                        textAlign: 'right',
                        color: parseInt(row.ms) > 1000 ? (isDark ? '#f87171' : '#dc2626') : (isDark ? 'rgba(226,232,240,0.5)' : 'rgba(30,41,59,0.4)'),
                        fontWeight: parseInt(row.ms) > 1000 ? 600 : 400,
                      }}>{row.ms}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* Fade-out bottom row hint */}
              <div style={{
                height: '14px',
                background: `linear-gradient(to bottom, transparent, ${isDark ? 'rgba(22,26,35,0.92)' : 'rgba(255,255,255,0.94)'})`,
                marginTop: '-14px',
                position: 'relative',
              }} />
            </div>
            {/* Footer */}
            <div style={{
              padding: '4px 10px 6px',
              borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'}`,
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}>
              <div style={{
                width: '5px',
                height: '5px',
                borderRadius: '50%',
                background: '#22c55e',
                boxShadow: '0 0 4px rgba(34,197,94,0.5)',
              }} />
              <span style={{ fontSize: '8px', color: t.textMuted }}>
                streaming • grouped by <span style={{ color: isDark ? '#93c5fd' : '#2563eb', fontWeight: 500 }}>{SAMPLE_GROUPINGS[sampleGroupIdx]}</span>
              </span>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes blink { 50% { opacity: 0; } }
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Header */}
      <div style={{
        position: 'absolute',
        top: '16px',
        left: '20px',
        color: t.text,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        zIndex: 100,
      }}>
        <h1 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: theme === 'dark' ? '#fff' : '#1d1d1f' }}>
          Visualize
        </h1>
        <p style={{ margin: '4px 0 0', color: t.textMuted, fontSize: '12px' }}>
          {allData.length > 0
            ? `${allData.length.toLocaleString()} rows • Grouped by ${xAxis || 'none'}`
            : `${SAMPLE_DATA.length.toLocaleString()} sample IIS logs • Grouped by ${SAMPLE_GROUPINGS[sampleGroupIdx]}`}
        </p>
      </div>

      {/* Query Panel */}
      <QueryPanel
        files={files.map(f => ({ name: f.fileName, key: f.s3Key || f.fileName, isMultipart: f.isMultipart }))}
        selectedFile={selectedFile}
        onFileSelect={handleFileSelect}
        query={query}
        onQueryChange={setQuery}
        onExecute={handleExecuteQuery}
        isLoading={isLoadingQuery}
        filesLoading={filesLoading}
        theme={theme}
        recordCount={allData.length > 0 ? allData.length : undefined}
        hasData={allData.length > 0}
        columns={availableColumns}
        isLoadingColumns={isLoadingColumns}
      />

      {/* Control Panels (shown when data loaded) */}
      {allData.length > 0 && columns.length > 0 && (
        <>
          <ViewControls
            settings={viewSettings}
            onChange={handleViewChange}
            onResetCamera={handleResetCamera}
            theme={theme}
            onThemeChange={setVizTheme}
            metallicMode={metallicMode}
            onMetallicChange={setMetallicMode}
            shapeType={shapeType}
            onShapeChange={setShapeType}
          />

          <CameraControls controlsRef={controlsRef} theme={theme} />

          <ControlPanel
            layout={layout}
            xAxis={xAxis}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            colorColumn={colorColumn}
            colorPalette={colorPalette}
            searchQuery={searchQuery}
            onLayoutChange={setLayout}
            onXAxisChange={setXAxis}
            onSortChange={(col, dir) => { setSortColumn(col); setSortDirection(dir); }}
            onColorChange={setColorColumn}
            onPaletteChange={setColorPalette}
            onSearchChange={setSearchQuery}
            searchMatchCount={searchMatchCount}
            columns={columns}
            columnCardinalities={columnCardinalities}
            theme={theme}
            scatterXColumn={scatterXColumn}
            scatterYColumn={scatterYColumn}
            onScatterXChange={setScatterXColumn}
            onScatterYChange={setScatterYColumn}
            sizeByColumn={sizeByColumn}
            onSizeByColumnChange={setSizeByColumn}
            sizeByDimension={sizeByDimension}
            onSizeByDimensionChange={setSizeByDimension}
            maxSizeMultiplier={maxSizeMultiplier}
            onMaxSizeChange={setMaxSizeMultiplier}
            shapeType={shapeType}
            numericColumns={numericColumns}
          />

          <DataOverlay
            data={selectedData}
            onClose={() => { setSelectedData(null); setIsDataOverlayMinimized(false); }}
            theme={theme}
            isMinimized={isDataOverlayMinimized}
            onMinimize={setIsDataOverlayMinimized}
            searchTerms={searchQuery ? [searchQuery] : []}
            pinnedSearches={pinnedSearches}
          />

          <HoverCard
            data={hoveredData}
            mousePos={mousePos}
            theme={theme}
            isVisible={hoverCardVisible}
            searchTerms={searchQuery ? [searchQuery] : []}
            pinnedSearches={pinnedSearches}
          />
          <FullDetailOverlay
            data={fullDetailData}
            onClose={handleCloseFullDetail}
            theme={theme}
            searchTerms={searchQuery ? [searchQuery] : []}
            pinnedSearches={pinnedSearches}
          />

          {/* Bottom search bar with pinned tags */}
          <div style={{
            position: 'absolute',
            bottom: '24px',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '8px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            zIndex: 100,
          }}>
            {/* Pinned search tags */}
            {pinnedSearches.length > 0 && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                flexWrap: 'wrap',
                justifyContent: 'center',
                maxWidth: '600px',
              }}>
                {pinnedSearches.map((pinned) => (
                  <div
                    key={pinned.term}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      background: t.panelBg,
                      border: `2px solid ${pinned.color}`,
                      borderRadius: '16px',
                      padding: '4px 10px',
                      backdropFilter: 'blur(20px)',
                      boxShadow: `0 2px 8px ${pinned.color}40`,
                    }}
                  >
                    <div
                      style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: pinned.color,
                        flexShrink: 0,
                      }}
                    />
                    <span style={{ fontSize: '11px', color: t.text, fontWeight: 500 }}>
                      {pinned.term}
                    </span>
                    <span style={{ fontSize: '9px', color: t.textMuted }}>
                      {(pinnedMatchCounts[pinned.term] || 0).toLocaleString()}
                    </span>
                    <button
                      onClick={() => handleRemovePinnedSearch(pinned.term)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: t.textMuted,
                        cursor: 'pointer',
                        padding: '0',
                        fontSize: '14px',
                        lineHeight: 1,
                        marginLeft: '2px',
                      }}
                      title="Remove"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Search input row */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}>
              {/* Search input with pin button */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                background: t.panelBg,
                border: `1px solid ${t.panelBorder}`,
                borderRadius: '6px',
                padding: '0 12px',
                backdropFilter: 'blur(20px)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={t.textMuted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px', flexShrink: 0 }}>
                  <circle cx="11" cy="11" r="8"/>
                  <path d="m21 21-4.35-4.35"/>
                </svg>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && searchQuery.trim()) {
                      handlePinSearch();
                    }
                  }}
                  placeholder="Type & Enter to pin..."
                  style={{
                    width: '160px',
                    padding: '8px 0',
                    background: 'transparent',
                    border: 'none',
                    color: t.text,
                    fontSize: '11px',
                    outline: 'none',
                  }}
                />
                {searchQuery && (
                  <>
                    <span style={{ fontSize: '10px', color: t.textMuted, marginRight: '4px' }}>
                      {searchMatchCount.toLocaleString()}
                    </span>
                    <button
                      onClick={handlePinSearch}
                      style={{
                        background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                        border: 'none',
                        color: '#fff',
                        cursor: 'pointer',
                        padding: '3px 8px',
                        fontSize: '9px',
                        fontWeight: 600,
                        borderRadius: '4px',
                        marginRight: '4px',
                      }}
                      title="Pin this search (Enter)"
                    >
                      PIN
                    </button>
                    <button
                      onClick={() => setSearchQuery('')}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: t.textMuted,
                        cursor: 'pointer',
                        padding: '2px',
                        fontSize: '12px',
                        lineHeight: 1,
                      }}
                    >
                      ×
                    </button>
                  </>
                )}
              </div>

              {/* Magnification slider */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                background: t.panelBg,
                border: `1px solid ${t.panelBorder}`,
                borderRadius: '6px',
                padding: '6px 10px',
                backdropFilter: 'blur(20px)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              }}>
                <input
                  type="range"
                  min="1"
                  max="4"
                  step="0.1"
                  value={cubeMagnification}
                  onChange={(e) => setCubeMagnification(parseFloat(e.target.value))}
                  style={{
                    width: '60px',
                    height: '3px',
                    cursor: 'pointer',
                    accentColor: t.textMuted,
                  }}
                />
                <span style={{ color: t.textMuted, fontSize: '10px', minWidth: '24px' }}>
                  {cubeMagnification.toFixed(1)}x
                </span>
              </div>
            </div>
          </div>
        </>
      )}


      {/* Help button */}
      <button
        onClick={() => setShowHelp(true)}
        style={{
          position: 'absolute',
          bottom: '24px',
          right: '24px',
          width: '44px',
          height: '44px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #ff9500 0%, #ff6b00 100%)',
          border: 'none',
          color: '#fff',
          fontSize: '18px',
          fontWeight: 700,
          cursor: 'pointer',
          boxShadow: '0 4px 16px rgba(255, 149, 0, 0.4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          transition: 'transform 0.2s, box-shadow 0.2s',
          zIndex: 100,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.1)';
          e.currentTarget.style.boxShadow = '0 6px 24px rgba(255, 149, 0, 0.5)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = '0 4px 16px rgba(255, 149, 0, 0.4)';
        }}
        title="Help"
      >
        ?
      </button>

      {/* Help Overlay - Line and Dot Based */}
      {showHelp && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.85)',
            zIndex: 10000,
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            cursor: 'pointer',
          }}
          onClick={() => setShowHelp(false)}
        >
          {/* Close hint */}
          <div style={{
            position: 'absolute',
            top: '20px',
            right: '24px',
            color: 'rgba(255,255,255,0.5)',
            fontSize: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            <span>Click anywhere to close</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </div>

          {/* Center - Mouse Controls */}
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
          }}>
            {/* Rotating circle indicator */}
            <svg width="120" height="120" viewBox="0 0 120 120" style={{ marginBottom: '16px' }}>
              <circle cx="60" cy="60" r="50" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="1" strokeDasharray="4 4" />
              <circle cx="60" cy="60" r="35" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
              <circle cx="60" cy="60" r="4" fill="rgba(255,255,255,0.6)" />
              {/* Rotation arrows */}
              <path d="M60 20 L55 28 M60 20 L65 28" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5" fill="none" />
              <path d="M100 60 L92 55 M100 60 L92 65" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5" fill="none" />
            </svg>
            <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
              Drag to rotate
            </div>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px', lineHeight: 1.6 }}>
              Left click + drag to orbit<br />
              Right click + drag to pan<br />
              Scroll to zoom
            </div>
          </div>

          {/* Top Left - Data Source */}
          <div style={{ position: 'absolute', top: '100px', left: '420px' }}>
            <svg width="80" height="40" viewBox="0 0 80 40">
              <circle cx="4" cy="20" r="3" fill="#3b82f6" />
              <line x1="7" y1="20" x2="80" y2="20" stroke="#3b82f6" strokeWidth="1" strokeDasharray="3 3" />
            </svg>
            <div style={{ color: '#3b82f6', fontSize: '11px', fontWeight: 500, marginTop: '-8px', marginLeft: '12px' }}>
              DATA SOURCE
            </div>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '10px', marginLeft: '12px', marginTop: '4px' }}>
              Select file & run query
            </div>
          </div>

          {/* Top Right - View Controls */}
          <div style={{ position: 'absolute', top: '100px', right: '200px', textAlign: 'right' }}>
            <svg width="80" height="40" viewBox="0 0 80 40">
              <circle cx="76" cy="20" r="3" fill="#10b981" />
              <line x1="0" y1="20" x2="73" y2="20" stroke="#10b981" strokeWidth="1" strokeDasharray="3 3" />
            </svg>
            <div style={{ color: '#10b981', fontSize: '11px', fontWeight: 500, marginTop: '-8px', marginRight: '12px' }}>
              VIEW CONTROLS
            </div>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '10px', marginRight: '12px', marginTop: '4px' }}>
              Theme, effects, shape
            </div>
          </div>

          {/* Right side - Layout Controls */}
          <div style={{ position: 'absolute', top: '50%', right: '200px', transform: 'translateY(-50%)', textAlign: 'right' }}>
            <svg width="80" height="40" viewBox="0 0 80 40">
              <circle cx="76" cy="20" r="3" fill="#f59e0b" />
              <line x1="0" y1="20" x2="73" y2="20" stroke="#f59e0b" strokeWidth="1" strokeDasharray="3 3" />
            </svg>
            <div style={{ color: '#f59e0b', fontSize: '11px', fontWeight: 500, marginTop: '-8px', marginRight: '12px' }}>
              LAYOUT & GROUPING
            </div>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '10px', marginRight: '12px', marginTop: '4px' }}>
              Bars, donut, scatter, etc.
            </div>
          </div>

          {/* Bottom Center - Search */}
          <div style={{ position: 'absolute', bottom: '80px', left: '50%', transform: 'translateX(-50%)', textAlign: 'center' }}>
            <svg width="40" height="50" viewBox="0 0 40 50">
              <circle cx="20" cy="46" r="3" fill="#8b5cf6" />
              <line x1="20" y1="0" x2="20" y2="43" stroke="#8b5cf6" strokeWidth="1" strokeDasharray="3 3" />
            </svg>
            <div style={{ color: '#8b5cf6', fontSize: '11px', fontWeight: 500, marginTop: '4px' }}>
              SEARCH & FILTER
            </div>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '10px', marginTop: '4px' }}>
              Type to highlight matches
            </div>
          </div>

          {/* Left side - Click hints */}
          <div style={{ position: 'absolute', top: '50%', left: '60px', transform: 'translateY(-50%)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                border: '2px solid rgba(255,255,255,0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="2">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </div>
              <div>
                <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '12px', fontWeight: 500 }}>Click</div>
                <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '10px' }}>Select item</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                border: '2px solid rgba(255,255,255,0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px',
                color: 'rgba(255,255,255,0.5)',
              }}>
                <span style={{ fontWeight: 600 }}>+</span>
              </div>
              <div>
                <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '12px', fontWeight: 500 }}>Shift + Click</div>
                <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '10px' }}>Full details</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                border: '2px solid rgba(255,255,255,0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '9px',
                color: 'rgba(255,255,255,0.5)',
              }}>
                ESC
              </div>
              <div>
                <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '12px', fontWeight: 500 }}>Escape</div>
                <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '10px' }}>Close overlays</div>
              </div>
            </div>
          </div>

          {/* Camera controls hint - bottom right */}
          <div style={{ position: 'absolute', bottom: '80px', right: '200px', textAlign: 'right' }}>
            <svg width="80" height="50" viewBox="0 0 80 50">
              <circle cx="76" cy="46" r="3" fill="#ec4899" />
              <line x1="0" y1="46" x2="73" y2="46" stroke="#ec4899" strokeWidth="1" strokeDasharray="3 3" />
            </svg>
            <div style={{ color: '#ec4899', fontSize: '11px', fontWeight: 500, marginTop: '4px', marginRight: '12px' }}>
              CAMERA PRESETS
            </div>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '10px', marginRight: '12px', marginTop: '4px' }}>
              Quick view angles
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
}
